import React, { useCallback } from 'react';

import { Icon, Typography } from '@rbilabs/universal-components';
import { useIsFocused } from '@react-navigation/native';
import { useRouter } from 'expo-router';
import { ClickEventComponentNames } from 'features/analytics/constants';
import { useCartItemsCount } from 'features/cart/hooks/use-cart-items-count';
import { logButtonClick } from 'features/navigation/utils/analytics';
import { PixelRatio, StyleSheet, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { fontHeadingStyles } from 'ucl/components/text/Typography';

import ActionButton, { ActionButtonSizes } from 'components/action-button';
import useIsSignUpAfterCartEnabled from 'hooks/use-is-sign-up-after-cart';
import { useIsDesktopBreakpoint } from 'hooks/use-media-query';
import { useAuthContext } from 'state/auth';
import { RunningExperiments, useExperiments } from 'state/experiments';
import { t } from 'state/intl/translations';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { ZIndex, primitive, spacing } from 'styles/constants';
import { HapticsNotificationType, hapticNotification } from 'utils/haptic';
import { TraceName } from 'utils/performance';
import { startTrace } from 'utils/performance/trace';
import { routes } from 'utils/routing';

type FloatingViewCartFabProps = {
  safeAreaBottom?: boolean;
  extraBottomInset?: number;
};

/**
 * The FAB is rendered as a persistent overlay inside multiple tab layouts. When
 * the screen it lives on is not focused, it must not subscribe to cart state or
 * re-render: otherwise stacked/inactive instances all recompute on every cart
 * change, scaling work with navigation depth. Gating on focus keeps only the
 * visible FAB live.
 */
export const FloatingViewCartFab = ({
  safeAreaBottom = false,
  extraBottomInset = 0,
}: FloatingViewCartFabProps) => {
  const isFocused = useIsFocused();

  if (!isFocused) {
    return null;
  }

  return (
    <FloatingViewCartFabInner safeAreaBottom={safeAreaBottom} extraBottomInset={extraBottomInset} />
  );
};

const FloatingViewCartFabInner = ({
  safeAreaBottom = false,
  extraBottomInset = 0,
}: FloatingViewCartFabProps) => {
  const safeAreaBottomInset = useSafeAreaInsets()?.bottom || 0;
  const { isAuthenticated, openSignInModal } = useAuthContext();
  const isSignUpAfterCartEnabled = useIsSignUpAfterCartEnabled();
  const enableMainFabButton = useFlag(LaunchDarklyFlag.ENABLE_MAIN_FAB_BUTTON);
  const isDesktop = useIsDesktopBreakpoint();
  const numberOfItemsInCart = useCartItemsCount();
  const router = useRouter();
  const { logExposure } = useExperiments();

  const onPress = useCallback(() => {
    logButtonClick({
      attributes: {
        component: ClickEventComponentNames.BUTTON,
        name: 'View Cart',
      },
    });

    if (!isAuthenticated) {
      logExposure(RunningExperiments.SignInUpAfterCart);
    }

    if (isAuthenticated || isSignUpAfterCartEnabled) {
      hapticNotification({ type: HapticsNotificationType.SUCCESS });
      startTrace(TraceName.CART_LOAD_TO_CONTINUE_LOADED);
      router.navigate(routes.cart);
    } else {
      openSignInModal({
        navigateOnSuccessRoute: routes.cart,
      });
    }
  }, [isAuthenticated, isSignUpAfterCartEnabled, router, openSignInModal, logExposure]);

  if (numberOfItemsInCart < 1 || !enableMainFabButton || isDesktop) {
    return null;
  }

  const fabCaption = t('viewCart');

  return (
    <View
      style={[
        styles.fabContainer,
        {
          paddingBottom: spacing.$7 + (safeAreaBottom ? safeAreaBottomInset : 0) + extraBottomInset,
        },
      ]}
    >
      {numberOfItemsInCart && (
        <Typography fontSize="xs" testID="items-in-cart-count" style={styles.badge}>
          {numberOfItemsInCart}
        </Typography>
      )}
      <ActionButton
        testID="nav-view-cart"
        keepIconSize
        size={ActionButtonSizes.LARGE}
        shadow="7"
        paddingLeft="$6"
        paddingRight="$6"
        backgroundColor={primitive.$fieryRed}
        _text={{ fontSize: 'lg' }}
        _hover={{
          backgroundColor: primitive.$fieryRedHover,
        }}
        onPress={onPress}
        leftIcon={<Icon variant={'cart'} size={'sm'} />}
      >
        {fabCaption}
      </ActionButton>
    </View>
  );
};

const fontScale = PixelRatio.getFontScale();
const badgeSize = Math.round(20 * fontScale);

const styles = StyleSheet.create({
  fabContainer: {
    position: 'absolute',
    bottom: 0,
    right: 0,
    paddingRight: spacing.$5,
  },
  badge: {
    borderRadius: Math.max(primitive.$borderRadius, badgeSize / 2),
    backgroundColor: primitive.$bbqBrown,
    minHeight: badgeSize,
    minWidth: badgeSize,
    position: 'absolute',
    right: fontScale <= 1 ? 16 : fontScale * 4,
    top: fontScale <= 1 ? -2 : fontScale * -13,
    color: primitive.$white,
    textAlign: 'center',
    zIndex: ZIndex.TOP,
    ...fontHeadingStyles.regular,
  },
});
